import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "./dashboard.css";
import { AttendanceStatus } from "../../constants/constants";
import CheckInOut from "./check_in_out";
import { getDashboardData } from "../services/dashboardservice";
import { useAuth } from "../context/authcontext";

// India Standard Time is a fixed UTC+5:30 offset (no DST). Used below so
// "today" and "this month" always match the IST calendar date shown on
// screen, regardless of what timezone the browser/OS is actually set to —
// `new Date().toISOString()` alone gives the UTC date, which can be a day
// off from IST for part of the day (e.g. late evening IST is already the
// next day in UTC).
const IST_OFFSET_MINUTES = 5 * 60 + 30;
const getISTNow = () => new Date(Date.now() + IST_OFFSET_MINUTES * 60000);

/* =====================================================
   PRIORITY ICONS (inline SVG, no extra icon library)
===================================================== */
// eslint-disable-next-line no-unused-vars
function PriorityIcon({ priority }) {
  const p = priority?.toLowerCase();
  if (p === "high") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className="task-priority-icon">
        <path
          d="M12 2c1 3-2 4-2 7a4 4 0 1 0 8 0c0-1-.5-2-1-2 .3 2-1 3-2 3-1.5 0-2-1.2-1.5-2.5C14.2 6 13 4 12 2Z"
          fill="currentColor"
        />
      </svg>
    );
  }
  if (p === "medium") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className="task-priority-icon">
        <path d="M5 15h14M5 9h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      </svg>
    );
  }
  if (p === "low") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className="task-priority-icon">
        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" className="task-priority-icon">
      <circle cx="12" cy="12" r="3.2" fill="currentColor" />
    </svg>
  );
}

/* =====================================================
   REUSABLE PREMIUM SVG DONUT CHART
   - data: [{ key, label, value, color, onClick? }]
   - Gradient-filled ring, soft ambient shadow, and a
     clean center readout. No chart library dependency.
===================================================== */
function Donut({
  data,
  size = 168,
  thickness = 22,
  centerLabel,
  centerValue,
  emptyLabel = "No data",
}) {
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = data.reduce((sum, d) => sum + (d.value || 0), 0);
  const activeSegs = data.filter((d) => d.value > 0);
  const gapDeg = total > 0 && activeSegs.length > 1 ? 4 : 0;
  const gapLen = (gapDeg / 360) * circumference;

  let cumulative = 0;
  const segments = activeSegs.map((d, i) => {
    const fraction = total > 0 ? d.value / total : 0;
    const rawLength = fraction * circumference;
    const length = Math.max(rawLength - gapLen, 0);
    const dashoffset = -cumulative;
    cumulative += rawLength;
    return { ...d, length, dashoffset, gradId: `donutGrad-${d.key}-${i}` };
  });

  return (
    <div className="donut-wrap">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="donut-svg"
      >
        <defs>
          {segments.map((seg) => (
            <linearGradient
              key={seg.gradId}
              id={seg.gradId}
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor={`color-mix(in srgb, ${seg.color} 78%, white)`} />
              <stop offset="100%" stopColor={seg.color} />
            </linearGradient>
          ))}
          <filter id="donutSoftShadow" x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="3" stdDeviation="6" floodColor="rgba(15,23,42,0.16)" />
          </filter>
        </defs>

        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--bg-main)"
          strokeWidth={thickness}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--border-color)"
          strokeWidth={1}
        />

        {total > 0 ? (
          <g
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            filter="url(#donutSoftShadow)"
          >
            {segments.map((seg) => (
              <circle
                key={seg.key}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={`url(#${seg.gradId})`}
                strokeWidth={thickness}
                strokeDasharray={`${seg.length} ${circumference - seg.length}`}
                strokeDashoffset={seg.dashoffset}
                strokeLinecap="round"
                className={seg.onClick ? "donut-seg donut-seg-clickable" : "donut-seg"}
                onClick={seg.onClick}
              >
                <title>{`${seg.label}: ${seg.value}`}</title>
              </circle>
            ))}
          </g>
        ) : null}

        <text x="50%" y="46%" textAnchor="middle" className="donut-center-value">
          {total > 0 ? centerValue ?? total : "—"}
        </text>
        <text x="50%" y="63%" textAnchor="middle" className="donut-center-label">
          {total > 0 ? centerLabel : emptyLabel}
        </text>
      </svg>
    </div>
  );
}

/* Legend rendered as premium pill chips, tinted by segment color */
function DonutLegend({ data }) {
  return (
    <ul className="donut-legend">
      {data.map((d) => (
        <li
          key={d.key}
          className={d.onClick ? "donut-legend-item donut-legend-clickable" : "donut-legend-item"}
          style={{ "--chip-color": d.color }}
          onClick={d.onClick}
        >
          <span className="donut-legend-dot" />
          <span className="donut-legend-text">{d.label}</span>
          <span className="donut-legend-value">{d.value}</span>
        </li>
      ))}
    </ul>
  );
}

/* Chart + stat-row breakdown — the "Today's Workforce Attendance" layout,
   reused for "My Attendance This Month" too so both panels read as one
   consistent design language. */
function DonutStatPanel({ data, size = 200, thickness = 26, centerValue, centerLabel }) {
  const total = data.reduce((sum, d) => sum + (d.value || 0), 0);
  return (
    <div className="hr-overview-body">
      <div className="hr-overview-chart">
        <Donut
          data={data}
          size={size}
          thickness={thickness}
          centerValue={centerValue ?? total}
          centerLabel={centerLabel}
        />
      </div>
      <div className="hr-overview-stats">
        {data.map((d) => {
          const pct = total > 0 ? Math.round((d.value / total) * 100) : 0;
          return (
            <div
              key={d.key}
              className={d.onClick ? "hr-stat-row hr-stat-row-clickable" : "hr-stat-row"}
              style={{ "--stat-color": d.color }}
              onClick={d.onClick}
            >
              <div className="hr-stat-top">
                <span className="hr-stat-dot" />
                <span className="hr-stat-label">{d.label}</span>
                <span className="hr-stat-pct">{pct}%</span>
                <span className="hr-stat-value">{d.value}</span>
              </div>
              <div className="hr-stat-bar-track">
                <div className="hr-stat-bar-fill" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Used to pin "today" quick-links (HR overview) to today's date,
  // as opposed to the "My Attendance This Month" links which intentionally
  // browse the whole month with no date filter.
  // Computed from IST (see getISTNow above), not the browser's local
  // timezone or a raw UTC ISO string — otherwise this can point at the
  // wrong calendar date for part of the day.
  const todayISO = getISTNow().toISOString().split("T")[0];

  // The logged-in user's own employee id, used to scope "My Attendance
  // This Month" clicks to just their own records. Falls back through a
  // couple of likely field names in case the employee_details shape
  // varies by endpoint.
  const ownEmployeeId =
    user?.employee_details?.id ??
    user?.employee_details?.employee_id ??
    user?.employee_id ??
    null;


  const ATTENDANCE_STATUS_MAP = {
  present: AttendanceStatus.PRESENT,
  absent: AttendanceStatus.ABSENT,
  halfDay: AttendanceStatus.HALF_DAY,
  leave: AttendanceStatus.ON_LEAVE,
  wfh: AttendanceStatus.WORK_FROM_HOME,
};

  const fetchDashboard = async () => {
    try {
      const res = await getDashboardData();
      setDashData(res.data || res);
    } catch (err) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  /* Navigate to Attendance module, optionally pre-filtered by status, date,
     month, and/or the logged-in employee (must match the Dropdown options
     in Attendance.js: Present, Absent, Half Day, Leave, WFH, Holiday,
     Weekend).
     - { today: true }: quick-links representing a live "today" snapshot
       (HR Attendance Overview) so the Attendance page opens scoped to
       today's date across everyone, not the whole log.
     - { month: true }: "My Attendance This Month" links — scoped to the
       current calendar month.
     - { mine: true }: adds the logged-in user's own employee id, so "My
       Attendance This Month" only shows their own records instead of
       every employee's. Not used for HR overview links, which are
       intentionally company-wide. */
const handleAttendanceClick = (
  status,
  { today = false, month = false, mine = false } = {}
) => {
  const params = new URLSearchParams();

  if (status) params.set("status", status);

  if (today) {
    params.set("date", todayISO);
  }

  if (month) {
    // IST-based "now" so the month boundary lines up with the IST date
    // shown everywhere else, not the browser's local timezone.
    const istNow = getISTNow();
    const currentMonth = `${istNow.getUTCFullYear()}-${String(
      istNow.getUTCMonth() + 1
    ).padStart(2, "0")}`;

    params.set("month", currentMonth);
  }

  if (mine && ownEmployeeId) {
    params.set("employee", ownEmployeeId);
  }

  navigate(`/attendance?${params.toString()}`);
};

  /* Navigate to Employee module, optionally pre-filtered by status
     (must match the Dropdown options in Employee.js: Active, Inactive,
     On Leave, Resigned, Terminated) */
  const handleEmployeeClick = (status) => {
    if (hasPermission("employee.view")) {
      navigate(status ? `/employee?status=${encodeURIComponent(status)}` : "/employee");
    }
  };

  /* Navigate to Task module, optionally pre-filtered by priority
     (must match the Dropdown options in Task.js: Low, Medium, High) */
  // eslint-disable-next-line no-unused-vars
  const handleTaskClick = (priority) => {
    if (hasPermission("task.view")) {
      navigate(priority ? `/task?priority=${encodeURIComponent(priority)}` : "/task");
    }
  };

  /* Navigate to Task module, optionally pre-filtered by status
     (Todo, In Progress, Done) — kept separate from handleTaskClick since
     that one filters by priority, not status. */
  // eslint-disable-next-line no-unused-vars
  const handleTaskStatusClick = (status) => {
    if (hasPermission("task.view")) {
      navigate(status ? `/task?status=${encodeURIComponent(status)}` : "/task");
    }
  };

  /* Navigate to Project module, if the module/permission exists */
  // eslint-disable-next-line no-unused-vars
  const handleProjectClick = () => {
    if (hasPermission("project.view")) {
      navigate("/project");
    }
  };

  const greeting = dashData?.greeting || `Welcome, ${user?.full_name || "User"}`;
  const attendance = dashData?.attendance_summary || {};
  const companyStats = dashData?.company_stats || null;
  const hrOverview = dashData?.hr_attendance_overview || null;
  // eslint-disable-next-line no-unused-vars
  const recentTasks = [
    ...(dashData?.tasks_summary?.in_progress || []),
    ...(dashData?.tasks_summary?.pending || [])
  ].slice(0, 6);

  /* ---- My Attendance This Month — donut data ---- */
  const presentDays = attendance.present ?? attendance.present_days ?? 0;
  const wfhDays = attendance.wfh ?? attendance.wfh_days ?? 0;
  const leaveDays = attendance.leave ?? attendance.on_leave ?? attendance.leave_days ?? 0;

  // FIX: "Half Day" and "Late" used to be summed into one clickable
  // segment (`halfLateDays = half_day + late`), but there is no LATE
  // status id in AttendanceStatus — "late" is a flag on Present records,
  // not its own status. Clicking that combined segment always sent
  // status=HALF_DAY (3) to the Attendance log, so whenever the count was
  // actually made up of "late" entries (or a mix), the log query legally
  // found fewer/no rows for status=3 and rendered "No Attendance Found"
  // even though the donut showed a non-zero number.
  //
  // Kept the two counts separate: `halfDays` drives the "Half Day"
  // segment and its click-through (status=HALF_DAY, so the number shown
  // always matches what the Attendance page will find). `lateDays` is
  // shown as its own read-only segment (no onClick) since there's no
  // status id to filter the Attendance log by "late" yet — wire that up
  // once the backend/Attendance page supports a late-arrival filter.
  const halfDays = attendance.half_day ?? 0;
  // const lateDays = attendance.late ?? 0;

  // All segments below now pass { month: true, mine: true } — mine
  // scopes the click-through to the logged-in employee's own attendance
  // records (via ownEmployeeId), so "My Attendance This Month" always
  // lands on just their own data for the current month + status, instead
  // of the whole company's.
  const myAttendanceDonutData = [
  {
    key: "present",
    label: "Present",
    value: presentDays,
    color: "#2563EB", // Royal Blue
   onClick: () =>
  handleAttendanceClick(ATTENDANCE_STATUS_MAP.present, {
    month: true,
    mine: true,
  })
  },
  {
    key: "wfh",
    label: "Work From Home",
    value: wfhDays,
    color: "#0EA5A4", // Premium Teal
   onClick: () => handleAttendanceClick(ATTENDANCE_STATUS_MAP.wfh, {
    month: true,
    mine: true,
  }),
  },
  {
    key: "leave",
    label: "Leave",
    value: leaveDays,
    color: "#F59E0B", // Amber
    onClick: () => handleAttendanceClick(ATTENDANCE_STATUS_MAP.leave, {
    month: true,
    mine: true,
  }),
  },
  {
    key: "half_day",
    label: "Half Day",
    value: halfDays,
    color: "#7C3AED", // Deep Violet
   onClick: () => handleAttendanceClick(ATTENDANCE_STATUS_MAP.halfDay, {
    month: true,
    mine: true,
  }),
  },
  // Read-only: no status id exists yet to filter the Attendance log by
  // "late", so this segment intentionally has no onClick. Only rendered
  // when there actually are late days, so it won't clutter the panel
  // for employees with zero.
  // ...(lateDays > 0
  //   ? [
  //       {
  //         key: "late",
  //         label: "Late",
  //         value: lateDays,
  //         color: "#EA580C", // Burnt Orange — distinct from Half Day's violet
  //       },
  //     ]
  //   : []),
];
  const myAttendanceTotal = presentDays ;

  /* ---- Company Overview — Totals donut (Employees / Projects / Tasks) ---- */
  const employeesTotal = companyStats?.employees?.total_active ?? 0;
  const projectsTotal = companyStats?.projects?.total ?? companyStats?.projects?.total_active ?? 0;

  const tasksTodo = companyStats?.tasks?.todo ?? 0;
  const tasksInProgress = companyStats?.tasks?.in_progress ?? 0;
  const tasksDone = companyStats?.tasks?.done ?? 0;
  // eslint-disable-next-line no-unused-vars
  const tasksHighPriority = companyStats?.tasks?.high_priority_open ?? 0;
  const tasksTotal = companyStats?.tasks?.total ?? (tasksTodo + tasksInProgress + tasksDone);

  const totalsDonutData = [
    {
      key: "employees",
      label: "Employees",
      value: employeesTotal,
      color: "var(--primary)",
      onClick: hasPermission("employee.view") ? () => handleEmployeeClick() : undefined,
    },
    // {
    //   key: "projects",
    //   label: "Projects",
    //   value: projectsTotal,
    //   color: "var(--info)",
    //   onClick: hasPermission("project.view") ? handleProjectClick : undefined,
    // },
    // {
    //   key: "tasks",
    //   label: "Tasks",
    //   value: tasksTotal,
    //   color: "var(--warning)",
    //   onClick: hasPermission("task.view") ? () => handleTaskClick() : undefined,
    // },
  ];

  /* ---- Company Overview — Task Status donut ---- */
  // const taskStatusDonutData = [
  //   {
  //     key: "todo",
  //     label: "To Do",
  //     value: tasksTodo,
  //     color: "var(--info)",
  //     onClick: hasPermission("task.view") ? () => handleTaskStatusClick("Todo") : undefined,
  //   },
  //   {
  //     key: "in_progress",
  //     label: "In Progress",
  //     value: tasksInProgress,
  //     color: "var(--primary)",
  //     onClick: hasPermission("task.view") ? () => handleTaskStatusClick("In Progress") : undefined,
  //   },
  //   {
  //     key: "done",
  //     label: "Done",
  //     value: tasksDone,
  //     color: "var(--success)",
  //     onClick: hasPermission("task.view") ? () => handleTaskStatusClick("Done") : undefined,
  //   },
  //   {
  //     key: "high_priority",
  //     label: "High Priority",
  //     value: tasksHighPriority,
  //     color: "#DC2626",
  //     onClick: hasPermission("task.view") ? () => handleTaskClick("High") : undefined,
  //   },
  // ];

  /* ---- HR Attendance Overview donut data ---- */
const hrPresent = hrOverview?.present ?? 0;
const hrAbsent = hrOverview?.absent ?? 0;

const hrWfh =
  hrOverview?.wfh ??
  hrOverview?.work_from_home ??
  hrOverview?.wfh_count ??
  0;

const hrHalfDay =
  hrOverview?.half_day ??
  hrOverview?.half_days ??
  hrOverview?.half_day_count ??
  0;

const hrLeave =
  hrOverview?.leave ??
  hrOverview?.on_leave ??
  0;

const hrNotMarked = hrOverview?.not_marked ?? 0;

 // Every segment here passes { today: true } — company-wide (no employee
 // filter), scoped to today's IST date + the clicked status. This part
 // was already correct in shape; the only real fix needed was making
 // todayISO itself IST-accurate (see getISTNow above) so "today" can't
 // drift a day off near midnight.
 const hrDonutData = [
  {
    key: "present",
    label: "Present",
    value: hrPresent,
    color: "var(--primary)",
  onClick: () =>
  handleAttendanceClick(ATTENDANCE_STATUS_MAP.present, { today: true })
  },
  {
    key: "absent",
    label: "Absent",
    value: hrAbsent,
    color: "#DC2626",
   onClick: () =>
  handleAttendanceClick(ATTENDANCE_STATUS_MAP.absent, { today: true })
  },
  {
    key: "wfh",
    label: "Work From Home",
    value: hrWfh,
    color: "#0EA5A4",
   onClick: () =>
  handleAttendanceClick(ATTENDANCE_STATUS_MAP.wfh, { today: true })
  },
  {
    key: "half_day",
    label: "Half Day",
    value: hrHalfDay,
    color: "#7C3AED",
    onClick: () =>
  handleAttendanceClick(ATTENDANCE_STATUS_MAP.halfDay, { today: true })
  },
  {
    key: "leave",
    label: "On Leave",
    value: hrLeave,
    color: "#F59E0B",
    onClick: () =>
  handleAttendanceClick(ATTENDANCE_STATUS_MAP.leave, { today: true })
  },
  {
    key: "not_marked",
    label: "Not Marked",
    value: hrNotMarked,
    color: "var(--text-muted)",
  },
];
const hrTotal =
  hrPresent +
  hrAbsent +
  hrWfh +
  hrHalfDay +
  hrLeave +
  hrNotMarked;

  return (
    <div className="dashboard-root">
      <div className="dash-greeting">
        <h2>{greeting}</h2>
      </div>

      {/* Pass fetchDashboard down so a check-in/check-out/break action can
          tell Dashboard to refetch its attendance_summary — otherwise
          CheckInOut's local state updates but dashData here stays stale
          until a full page refresh remounts this component. */}
      {user?.employee_details && <CheckInOut onAttendanceChange={fetchDashboard} />}

      {loading && (
        <div className="empty-state"><p>Loading dashboard…</p></div>
      )}

      {!loading && (
        <>
          {/* Personal Attendance Summary — same composed panel style as
              "Today's Workforce Attendance" (only for linked employees) */}
          {user?.employee_details && (
            <div className="attendance-card hr-overview-card">
              <div className="hr-overview-header">
                <div>
                  <h3>My Attendance This Month</h3>
                  <p className="hr-overview-sub">
                    {new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" })}
                  </p>
                </div>
                <span className="hr-live-pill">This Month</span>
              </div>

              <DonutStatPanel
                data={myAttendanceDonutData}
                centerValue={myAttendanceTotal}
                centerLabel="Total Days"
              />
            </div>
          )}

          {/* Company Overview — totals + task status donuts */}
          {companyStats && hasPermission("dashboard.view_company_stats") && (
            <div className="attendance-card">
              <h3>Company Overview</h3>
              <div className={hasPermission("task.view") ? "donut-grid" : "donut-grid donut-grid-single"}>
                <div className="donut-block">
                  <p className="donut-block-title">Organization Totals</p>
                  <Donut
                    data={totalsDonutData}
                    centerValue={employeesTotal + projectsTotal + tasksTotal}
                    centerLabel="Total"
                  />
                  <DonutLegend data={totalsDonutData} />
                </div>

                {/* {hasPermission("task.view") && (
                  <div className="donut-block">
                    <p className="donut-block-title">Task Status</p>
                    <Donut
                      data={taskStatusDonutData}
                      centerValue={tasksTodo + tasksInProgress + tasksDone}
                      centerLabel="Tasks"
                    />
                    <DonutLegend data={taskStatusDonutData} />
                  </div>
                )} */}
              </div>
            </div>
          )}

          {/* HR Attendance Overview — chart + stat breakdown, side by side
              so the card reads as one composed panel instead of a single
              small ring floating in a lot of empty space. */}
          {hrOverview && hasPermission("dashboard.view_hr_overview") && (
            <div className="attendance-card hr-overview-card">
              <div className="hr-overview-header">
                <div>
                  <h3>Today's Workforce Attendance</h3>
                  <p className="hr-overview-sub">
                    {new Date().toLocaleDateString(undefined, {
                      weekday: "long",
                      month: "short",
                      day: "numeric",
                    })}
                  </p>
                </div>
                <span className="hr-live-pill">
                  <span className="hr-live-dot" />
                  Live
                </span>
              </div>

              <DonutStatPanel
                data={hrDonutData}
                size={200}
                thickness={26}
                centerValue={hrTotal}
                centerLabel="Workforce"
              />
            </div>
          )}

          {/* My Tasks Summary — premium 3-up card grid */}
          {/* {hasPermission("task.view") && (
            <div className="task-card">
              <div className="task-card-header">
                <h3>My Tasks</h3>
                {recentTasks.length > 0 && (
                  <span className="task-card-count">{recentTasks.length}</span>
                )}
              </div>

              <div className="task-list">
                {recentTasks && recentTasks.length > 0 ? (
                  recentTasks.map((task, i) => {
                    const priorityKey = task.priority_display?.toLowerCase() || "normal";
                    const statusKey = task.status_display?.replace(" ", "-").toLowerCase() || "";
                    const isDone = task.status === 3; // TaskStatus.DONE is 3
                    const isOverdue =
                      !isDone &&
                      task.due_date &&
                      new Date(task.due_date) < new Date(new Date().toDateString());
                    return (
                      <div
                        key={task.id || i}
                        className={`task-item priority-${priorityKey}`}
                        style={{ "--stagger": i }}
                        onClick={() => navigate(`/task?taskId=${task.id}`)}
                      >
                        <div className="task-item-content">
                          <div className="task-item-top">
                            <p className="task-title">{task.title}</p>

                            <span className={`task-priority ${priorityKey}`}>
                              <PriorityIcon priority={priorityKey} />
                              {task.priority_display || task.priority}
                            </span>
                          </div>

                          {task.description && (
                            <p className="task-desc">{task.description}</p>
                          )}

                          <div className="task-meta">
                            <span className={`task-status ${statusKey}`}>
                              {task.status_display || task.status || "—"}
                            </span>

                            {task.due_date && (
                              <span className={isOverdue ? "task-due task-due-overdue" : "task-due"}>
                                <svg viewBox="0 0 24 24" fill="none" className="task-due-icon">
                                  <rect x="3.5" y="5" width="17" height="16" rx="3" stroke="currentColor" strokeWidth="1.8" />
                                  <path d="M3.5 9.5h17M8 3v3.5M16 3v3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                </svg>
                                {isOverdue ? "Overdue " : "Due "}
                                {new Date(task.due_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="task-empty">
                    <p>No tasks assigned to you at the moment.</p>
                  </div>
                )}
              </div>
            </div>
          )}*/}
        </>
      )} 
    </div>
  );
}